import { z } from 'zod';

import type { Appointment, AppointmentCancelledBy } from '../model/types';

/** Validated at the boundary, mapped straight to the entity type (§7.4). */
export const appointmentSchema = z.object({
  appointmentId: z.string().min(1),
  startsAt: z.string().datetime({ offset: true }),
  endsAt: z.string().datetime({ offset: true }),
  practitionerName: z.string(),
  feeMinorAmount: z.number().int().nonnegative(),
  // `.default(0)` rather than required: the tax fields arrived after the
  // endpoint did, and a client that is one deploy ahead of the API must render
  // a price rather than an error.
  taxMinorAmount: z.number().int().nonnegative().default(0),
  totalMinorAmount: z.number().int().nonnegative().optional(),
  currency: z.string(),
  receiptCode: z.string().nullable(),
  isUpcoming: z.boolean(),
  // Cancellation, as the *snapshotted* terms of this appointment decide it —
  // never today's settings. Optional for the same reason the tax fields are:
  // a client one deploy ahead of the API must still render the card.
  cancellationDeadline: z.string().datetime({ offset: true }).nullable().optional(),
  isCancellable: z.boolean().optional(),
  refundIfCancelledNowMinorAmount: z.number().int().nonnegative().optional(),
  // پیگیری. Optional and nullable for the same reason as the fields above: a
  // client one deploy ahead of the API renders the card without the action,
  // rather than failing the whole list on a missing key.
  isAttended: z.boolean().optional(),
  // when the desk admitted the patient — optional for a client one deploy
  // ahead of the API, which then shows «مراجعه شد» without a moment
  attendedAt: z.string().datetime({ offset: true }).nullable().optional(),
  // «ویزیت شد» — optional for the same reason as `attendedAt`
  prescribedAt: z.string().datetime({ offset: true }).nullable().optional(),
  isNoShow: z.boolean().optional(),
  // a consultation's «مدارک» thread holds an answer not yet opened; older servers omit it
  hasUnseenReply: z.boolean().optional(),
  // the bank's کد پیگیری; optional for a client one deploy ahead of the API
  paymentReference: z.string().nullable().optional(),
  // Cancellation. Optional and nullable like the fields above, so a client one
  // deploy ahead of the API renders the card rather than failing the list.
  isCancelled: z.boolean().optional(),
  cancelledAt: z.string().datetime({ offset: true }).nullable().optional(),
  // A plain string rather than an enum on purpose: an actor this build does not
  // know about must degrade to «لغو شده» (see `toCancelledBy`), never reject
  // the whole list because one row named someone new.
  cancelledBy: z.string().nullable().optional(),
  followUp: z
    .object({
      visitId: z.string().min(1),
      closesAt: z.string().datetime({ offset: true }),
      isOpen: z.boolean(),
      messageCount: z.number().int().nonnegative(),
      awaitsClinicReply: z.boolean(),
      // optional: a client one deploy ahead of the API shows no «جدید» rather
      // than failing the list
      hasUnseenReply: z.boolean().optional(),
    })
    .nullable()
    .optional(),
});

/**
 * The list arrives wrapped in an object rather than as a bare array, so there
 * is somewhere to put a cursor the day it needs one without breaking a shipped
 * shape.
 */
export const myAppointmentsSchema = z.object({
  items: z.array(appointmentSchema),
});

const CANCELLED_BY: readonly AppointmentCancelledBy[] = ['patient', 'clinic', 'system'];

/** Anything this build does not recognise becomes «no named actor», not a guess. */
function toCancelledBy(value: string | null | undefined): AppointmentCancelledBy | null {
  return CANCELLED_BY.find((one) => one === value) ?? null;
}

export function toAppointment(dto: z.infer<typeof appointmentSchema>): Appointment {
  return {
    id: dto.appointmentId,
    startsAt: new Date(dto.startsAt),
    endsAt: new Date(dto.endsAt),
    practitionerName: dto.practitionerName,
    feeMinorAmount: dto.feeMinorAmount,
    taxMinorAmount: dto.taxMinorAmount,
    // The server's sum wins where it is given: the patient was charged its
    // arithmetic, not the client's.
    totalMinorAmount: dto.totalMinorAmount ?? dto.feeMinorAmount + dto.taxMinorAmount,
    currency: dto.currency,
    receiptCode: dto.receiptCode,
    isUpcoming: dto.isUpcoming,
    cancellationDeadline:
      dto.cancellationDeadline == null ? null : new Date(dto.cancellationDeadline),
    // Absent means an older API that cannot cancel at all — never "yes".
    isCancellable: dto.isCancellable ?? false,
    refundIfCancelledNowMinorAmount: dto.refundIfCancelledNowMinorAmount ?? 0,
    isAttended: dto.isAttended ?? false,
    attendedAt: dto.attendedAt == null ? null : new Date(dto.attendedAt),
    prescribedAt: dto.prescribedAt == null ? null : new Date(dto.prescribedAt),
    isNoShow: dto.isNoShow ?? false,
    hasUnseenReply: dto.hasUnseenReply ?? false,
    paymentReference: dto.paymentReference ?? null,
    // Absent means an older API that never marks a row cancelled — and one that
    // does not send cancelled rows at all, so `false` is the truthful default.
    isCancelled: dto.isCancelled ?? false,
    cancelledAt: dto.cancelledAt == null ? null : new Date(dto.cancelledAt),
    cancelledBy: toCancelledBy(dto.cancelledBy),
    followUp:
      dto.followUp == null
        ? null
        : {
            visitId: dto.followUp.visitId,
            closesAt: new Date(dto.followUp.closesAt),
            isOpen: dto.followUp.isOpen,
            messageCount: dto.followUp.messageCount,
            awaitsClinicReply: dto.followUp.awaitsClinicReply,
            hasUnseenReply: dto.followUp.hasUnseenReply ?? false,
          },
  };
}
